import { FunctionComponent, ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { faCheck, faUser } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import { Toggle } from "@/components/Toggle/Toggle";

import {
	useGenerateTwoFactorCodeMutation,
	useUpdateMeMutation,
} from "@/pages/Private/redux/account/account.api";

import i18n from "@/i18n";

import { UserLanguage } from "@/enum/user.enum";

import { UpdateProfile, UpdateProfileSchema } from "../schema/account";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { ChangeEmailModal } from "./ChangeEmailModal";
import { Enable2FAModal } from "./Enable2FAModal";
import { SettingsCard } from "./SettingsCard";

type ProfileCardProps = {
	firstName: string;
	lastName: string;
	linkedinUrl: string;
	email: string;
	is2FAEnabled: boolean;
	userLanguage: UserLanguage;
};

export const ProfileCard: FunctionComponent<ProfileCardProps> = ({
	firstName,
	lastName,
	linkedinUrl,
	email,
	is2FAEnabled,
	userLanguage,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.${key}`);

	const [updateMe, { isLoading }] = useUpdateMeMutation();
	const [generate, { data: twoFactorSetup, isLoading: isGeneratingCode }] =
		useGenerateTwoFactorCodeMutation();

	const [showPasswordModal, setShowPasswordModal] = useState(false);
	const [showEmailModal, setShowEmailModal] = useState(false);
	const [is2FAModalOpen, setIs2FAModalOpen] = useState(false);
	const [showSavedFlash, setShowSavedFlash] = useState(false);
	const savedFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;

		return () => {
			mountedRef.current = false;

			if (savedFlashTimerRef.current) {
				clearTimeout(savedFlashTimerRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (userLanguage) {
			i18n.changeLanguage(userLanguage.toLowerCase());
		}
	}, [userLanguage]);

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isDirty },
	} = useForm<UpdateProfile>({
		defaultValues: { firstName, lastName, linkedinUrl: linkedinUrl || "" },
		resolver: zodResolver(UpdateProfileSchema),
	});

	useEffect(() => {
		reset({ firstName, lastName, linkedinUrl: linkedinUrl || "" });
	}, [firstName, lastName, linkedinUrl, reset]);

	const onSubmit = async (values: UpdateProfile) => {
		try {
			await updateMe({
				firstName: values.firstName,
				lastName: values.lastName,
				linkedinUrl: values.linkedinUrl,
			}).unwrap();

			if (!mountedRef.current) {
				return;
			}

			reset(values);
			setShowSavedFlash(true);

			if (savedFlashTimerRef.current) {
				clearTimeout(savedFlashTimerRef.current);
			}

			savedFlashTimerRef.current = setTimeout(() => setShowSavedFlash(false), 1600);
		} catch (err) {
			console.error(err);
		}
	};

	// 2FA can't be toggled silently — enabling needs a scanned QR + verification code and
	// disabling needs a code — so the toggle opens the verification modal instead of autosaving.
	const handle2FAToggle = async () => {
		setIs2FAModalOpen(true);

		if (!is2FAEnabled) {
			await generate();
		}
	};

	const footer = (
		<>
			{showSavedFlash && (
				<span className="mr-1 flex items-center gap-1 text-[12.5px] font-semibold text-status-success">
					<Icon className="h-3.5 w-3.5" icon={faCheck} />
					{ts("saved")}
				</span>
			)}
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				disabled={!isDirty}
				size={ButtonSize.S}
				title={t("basics.cancel")}
				type="button"
				onClick={() => reset({ firstName, lastName, linkedinUrl: linkedinUrl || "" })}
			/>
			<Button
				color={ButtonColor.ACTION}
				disabled={!isDirty}
				isLoading={isLoading}
				size={ButtonSize.S}
				title={t("basics.saveChanges")}
				type="button"
				onClick={handleSubmit(onSubmit)}
			/>
		</>
	);

	return (
		<SettingsCard
			footer={footer}
			icon={faUser}
			subtitle={ts("profileInformationSubtitle")}
			title={ts("profileInformation")}
		>
			<div className="max-w-[460px] px-5 pt-4">
				<Controller
					control={control}
					name="firstName"
					render={({ field }) => (
						<InputField
							error={errors.firstName?.message}
							label={t("basics.firstName")}
							name="firstName"
							value={field.value ?? ""}
							handleChange={field.onChange}
						/>
					)}
				/>

				<Controller
					control={control}
					name="lastName"
					render={({ field }) => (
						<InputField
							error={errors.lastName?.message}
							label={t("basics.lastName")}
							name="lastName"
							value={field.value ?? ""}
							handleChange={field.onChange}
						/>
					)}
				/>

				<Controller
					control={control}
					name="linkedinUrl"
					render={({ field }) => (
						<InputField
							error={errors.linkedinUrl?.message}
							label={t("basics.linkedinUrl")}
							name="linkedinUrl"
							placeholder="https://linkedin.com/in/…"
							value={field.value ?? ""}
							handleChange={field.onChange}
						/>
					)}
				/>
			</div>

			<div className="mt-2 flex flex-col px-5">
				<ProfileRow label={t("basics.email")} value={email}>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.S}
						title={ts("changeEmail")}
						type="button"
						onClick={() => setShowEmailModal(true)}
					/>
				</ProfileRow>

				<ProfileRow label={t("basics.password")} value="••••••••••">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.S}
						title={ts("changePassword")}
						type="button"
						onClick={() => setShowPasswordModal(true)}
					/>
				</ProfileRow>

				<ProfileRow
					description={ts("enableTwoFactorAuthHint")}
					label={ts("twoFactorAuth")}
					leading={
						<Toggle
							isChecked={is2FAEnabled}
							name="2fa"
							testId="two-factor-toggle"
							onChange={handle2FAToggle}
						/>
					}
				/>
			</div>

			{showPasswordModal && (
				<ChangePasswordModal
					handleClose={() => setShowPasswordModal(false)}
					isVisible={showPasswordModal}
				/>
			)}

			{showEmailModal && (
				<ChangeEmailModal
					currentEmail={email}
					handleClose={() => setShowEmailModal(false)}
					isVisible={showEmailModal}
				/>
			)}

			{is2FAModalOpen && (
				<Enable2FAModal
					isLoading={isGeneratingCode}
					isOpen={is2FAModalOpen}
					qrCode={is2FAEnabled ? "" : (twoFactorSetup?.qrCode ?? "")}
					secret={is2FAEnabled ? "" : (twoFactorSetup?.secret ?? "")}
					setIsOpen={setIs2FAModalOpen}
				/>
			)}
		</SettingsCard>
	);
};

type ProfileRowProps = {
	label: string;
	/** Optional value shown under the label (mockup `pf-val`), e.g. the email address or masked password. */
	value?: ReactNode;
	description?: string;
	/** Optional control rendered to the left of the label (e.g. a toggle, mockup `toggle-left`). */
	leading?: ReactNode;
	children?: ReactNode;
};

const ProfileRow: FunctionComponent<ProfileRowProps> = ({
	label,
	value,
	description,
	leading,
	children,
}) => (
	<div className="flex items-center gap-4 border-t border-gray-100 py-4 first:border-t-0">
		{leading && <div className="flex flex-shrink-0 items-center">{leading}</div>}
		{/*
		 * A row with an action (Change email / Change password) caps its label+value
		 * column so the button sits next to the value instead of drifting to the far
		 * card edge (PREC-3686). Rows without one keep the full width for their text.
		 */}
		<div className={children ? "w-[240px] min-w-0 flex-shrink-0" : "min-w-0 flex-1"}>
			<div
				className={
					value ? "text-[11.5px] font-semibold text-text-meta" : "text-sm font-semibold text-text"
				}
			>
				{label}
			</div>
			{value && <div className="mt-1 text-sm font-medium text-text">{value}</div>}
			{description && <div className="mt-0.5 text-[12.5px] text-text-meta">{description}</div>}
		</div>
		{children && <div className="flex flex-shrink-0 items-center gap-3">{children}</div>}
	</div>
);
